/**
 * Excel context state detection.
 *
 * Reads the current Excel selection and produces a typed ExcelContextState
 * used for:
 *   - Dynamic ribbon button enable/disable (RibbonApi 1.1)
 *   - Taskpane context banner and suggestions
 *
 * All analysis runs in a single Excel.run() with at most 2 sync calls
 * (the second loads a capped sample of cell values for large selections).
 */

/* global Excel */

/** Typed context state produced by analyzing the current selection. */
export interface ExcelContextState {
  /** At least one non-empty cell in the selection. */
  readonly hasSelection: boolean;
  /** The active cell contains a formula (starts with =). */
  readonly isFormula: boolean;
  /** Selection has multiple rows and columns with data (table-like). */
  readonly isDataset: boolean;
  /** Majority of non-empty cells are text strings. */
  readonly isTextual: boolean;
  /** Dataset with enough numeric data to suggest a chart. */
  readonly canChart: boolean;
  /** The active sheet has a used range (contains data somewhere). */
  readonly sheetHasUsedRange: boolean;
  /** Number of rows in the selection. */
  readonly rowCount: number;
  /** Number of columns in the selection. */
  readonly columnCount: number;
  /** Number of non-empty cells in the analyzed sample. */
  readonly nonEmptyCellCount: number;
  /** A1-style address of the full selection (e.g. "Feuil1!A1:C10"). */
  readonly address: string;
  /** A1-style address of the active cell (e.g. "Feuil1!D4"). */
  readonly activeCellAddress: string;
  /** The formula in the active cell (English names), or null. */
  readonly formula: string | null;
  /** The formula in the active cell (localized names), or null. */
  readonly formulaLocal: string | null;
  /** Probable column headers from the first row (non-empty strings). */
  readonly probableHeaders: readonly string[];
}

/** Default empty context returned when detection fails or Excel is unavailable. */
export const EMPTY_CONTEXT: ExcelContextState = {
  hasSelection: false,
  isFormula: false,
  isDataset: false,
  isTextual: false,
  canChart: false,
  sheetHasUsedRange: false,
  rowCount: 0,
  columnCount: 0,
  nonEmptyCellCount: 0,
  address: '',
  activeCellAddress: '',
  formula: null,
  formulaLocal: null,
  probableHeaders: [],
};

const MAX_SAMPLE_ROWS = 100;
const MAX_SAMPLE_COLS = 50;

/**
 * Read and analyze the current Excel selection.
 *
 * Sync 1: load range metadata + active cell formula + used range check.
 * Sync 2: load a capped sample of cell values for content analysis.
 */
export async function readExcelContextState(): Promise<ExcelContextState> {
  if (typeof Excel === 'undefined') return EMPTY_CONTEXT;

  try {
    return await Excel.run(async (ctx) => {
      const fullRange = ctx.workbook.getSelectedRange();
      fullRange.load(['rowCount', 'columnCount', 'address']);

      const cell = fullRange.getCell(0, 0);
      cell.load(['formulas', 'formulasLocal', 'address']);

      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      const usedRange = sheet.getUsedRangeOrNullObject();
      usedRange.load('isNullObject');

      await ctx.sync();

      const rows = fullRange.rowCount;
      const cols = fullRange.columnCount;
      const address = fullRange.address ?? '';
      const activeCellAddress = cell.address ?? '';

      const rawFormula = (cell.formulas as unknown[][])[0]?.[0];
      const rawFormulaLocal = (cell.formulasLocal as unknown[][])[0]?.[0];
      const formula =
        typeof rawFormula === 'string' && rawFormula.startsWith('=') ? rawFormula : null;
      const formulaLocal =
        typeof rawFormulaLocal === 'string' && rawFormulaLocal.startsWith('=') ? rawFormulaLocal : null;
      const isFormula = formula !== null;

      const sheetHasUsedRange = !usedRange.isNullObject;

      // Load a capped sample of values for content analysis
      const sampleRows = Math.min(rows, MAX_SAMPLE_ROWS);
      const sampleCols = Math.min(cols, MAX_SAMPLE_COLS);

      const sampleRange =
        sampleRows < rows || sampleCols < cols
          ? fullRange.getCell(0, 0).getResizedRange(sampleRows - 1, sampleCols - 1)
          : fullRange;
      sampleRange.load('values');

      await ctx.sync();

      const values = sampleRange.values as unknown[][];
      let nonEmptyCount = 0;
      let textCount = 0;
      let numericCount = 0;

      for (const row of values) {
        for (const v of row) {
          if (v !== null && v !== undefined && v !== '') {
            nonEmptyCount++;
            if (typeof v === 'string' && v.trim().length > 0) {
              textCount++;
            } else if (typeof v === 'number') {
              numericCount++;
            }
          }
        }
      }

      const hasSelection = nonEmptyCount > 0;
      const isDataset = rows >= 2 && cols >= 2 && nonEmptyCount >= 4;
      const isTextual = hasSelection && textCount > numericCount && textCount >= 2;
      const canChart = isDataset && numericCount >= 2;

      // Extract probable headers from the first row if dataset-like
      let probableHeaders: string[] = [];
      if (isDataset && values.length > 0) {
        const firstRow = values[0];
        const headers = firstRow
          .map((v) => (typeof v === 'string' && v.trim().length > 0 ? v.trim() : ''))
          .filter(Boolean);
        if (headers.length >= Math.ceil(cols / 2)) {
          probableHeaders = headers;
        }
      }

      return {
        hasSelection,
        isFormula,
        isDataset,
        isTextual,
        canChart,
        sheetHasUsedRange,
        rowCount: rows,
        columnCount: cols,
        nonEmptyCellCount: nonEmptyCount,
        address,
        activeCellAddress,
        formula,
        formulaLocal,
        probableHeaders,
      };
    });
  } catch {
    return EMPTY_CONTEXT;
  }
}
